import type {
  ValidateSessionStatus,
  ValidateSetup,
  ValidateSubsystemHealthStatus,
} from '../types/validateDetails.types';
import { SESSION_STATUS_CSS } from '../constants/validateDetails.constants';

/**
 * Returns the correct empty-table message.
 * - No sessions exist at all → "No sessions available"
 * - Sessions exist but filtered out by search → "No sessions match your search"
 */
export function getEmptySessionsMessage(
  totalSessionsForSetup: number,
  hasSearchFilter: boolean,
): string {
  if (totalSessionsForSetup > 0 && hasSearchFilter) return 'No sessions match your search';
  return 'No sessions available';
}

/** CSS modifier class for session status badge (e.g. validate__status-badge--running). */
export function getSessionStatusBadgeClass(status: ValidateSessionStatus): string {
  return SESSION_STATUS_CSS[status] ?? '';
}

/** Whether a setup card is the currently active one. */
export function isCardActive(setup: ValidateSetup, activeSetup: ValidateSetup | null): boolean {
  return activeSetup?.setupId === setup.setupId;
}

/** Whether a setup has an active in-flight session (delete should be blocked). */
export function setupHasRunningSession(setup: ValidateSetup): boolean {
  return setup.sessions.some((s) => s.status === 'RUNNING' || s.status === 'DEPLOYING' || s.status === 'SCHEDULED');
}

/** Normalizes backend/setup-sanity subsystem status variants to UI health states. */
export function normalizeSubsystemHealthStatus(raw: unknown): ValidateSubsystemHealthStatus {
  const value = String(raw ?? '').trim().toUpperCase();

  switch (value) {
    case 'HEALTHY':
    case 'PASS':
    case 'PASSED':
    case 'CONNECTED':
      return 'HEALTHY';
    case 'UNHEALTHY':
    case 'FAIL':
    case 'FAILED':
    case 'DISCONNECTED':
      return 'UNHEALTHY';
    case 'PENDING':
    case 'RUNNING':
    case 'IN_PROGRESS':
      return 'PENDING';
    case 'UNKNOWN':
    default:
      return 'UNKNOWN';
  }
}
